/**
 * Date Picker Component
 *
 * A simple, accessible date picker that matches the warm design system.
 * Allows selecting dates up to today (no future dates for retrospective logging).
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { getToday, formatDate } from '@have-done/shared';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  disabled?: boolean;
  maxDate?: string;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  // Convert Sunday = 0 to Sunday = 6, Monday = 0
  return day === 0 ? 6 : day - 1;
}

function formatYYYYMMDD(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function DatePicker({ value, onChange, disabled = false, maxDate }: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const [year, month] = value.split('-').map(Number);
    return { year, month: month - 1 };
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const today = maxDate || getToday();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const { year, month } = viewDate;
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const goToPreviousMonth = () => {
    setViewDate(prev => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const nextMonthStr = formatYYYYMMDD(nextYear, nextMonth, 1);

    // Don't go past the max date's month
    if (nextMonthStr > today) return;

    setViewDate(prev => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { ...prev, month: prev.month + 1 };
    });
  };

  const handleDateSelect = (day: number) => {
    const dateStr = formatYYYYMMDD(year, month, day);
    if (dateStr <= today) {
      onChange(dateStr);
      setIsOpen(false);
    }
  };

  const isSelected = (day: number) => {
    return formatYYYYMMDD(year, month, day) === value;
  };

  const isDisabledDate = (day: number) => {
    return formatYYYYMMDD(year, month, day) > today;
  };

  const isToday = (day: number) => {
    return formatYYYYMMDD(year, month, day) === getToday();
  };

  // Check if we can go to next month
  const canGoNext = formatYYYYMMDD(
    month === 11 ? year + 1 : year,
    month === 11 ? 0 : month + 1,
    1
  ) <= today;

  // Format the display value
  const displayValue = value === getToday()
    ? 'Today'
    : formatDate(value, { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          inline-flex items-center gap-2 px-3 py-2 rounded-lg
          border border-border-light bg-bg-primary
          text-sm text-text-primary
          transition-all
          hover:border-primary/50 hover:bg-bg-secondary
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          ${isOpen ? 'border-primary ring-2 ring-primary/20' : ''}
        `}
        aria-label="Select date"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Calendar className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
        <span>{displayValue}</span>
      </button>

      {/* Calendar dropdown */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Choose date"
          className="
            absolute z-50 mt-2 p-4 rounded-xl
            bg-bg-primary border border-border-light shadow-warm-lg
            w-72
          "
        >
          {/* Month/Year navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="
                p-1.5 rounded-lg text-text-secondary
                hover:text-text-primary hover:bg-bg-secondary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              "
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="font-medium text-text-primary">
              {MONTHS[month]} {year}
            </span>
            <button
              type="button"
              onClick={goToNextMonth}
              disabled={!canGoNext}
              className="
                p-1.5 rounded-lg text-text-secondary
                hover:text-text-primary hover:bg-bg-secondary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent
              "
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map(day => (
              <div
                key={day}
                className="text-center text-xs font-medium text-text-tertiary py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before the first of the month */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}

            {/* Day buttons */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const disabled = isDisabledDate(day);
              const selected = isSelected(day);
              const todayClass = isToday(day);

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDateSelect(day)}
                  disabled={disabled}
                  className={`
                    aspect-square rounded-lg text-sm font-medium
                    transition-all
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                    ${selected
                      ? 'bg-primary text-white shadow-sm'
                      : disabled
                        ? 'text-text-tertiary/50 cursor-not-allowed'
                        : todayClass
                          ? 'bg-accent/20 text-accent-dark hover:bg-accent/30'
                          : 'text-text-primary hover:bg-bg-secondary'
                    }
                  `}
                  aria-label={formatYYYYMMDD(year, month, day)}
                  aria-pressed={selected}
                >
                  {day}
                </button>
              );
            })}
          </div>

          {/* Quick actions */}
          <div className="flex gap-2 mt-4 pt-4 border-t border-border-light">
            <button
              type="button"
              onClick={() => {
                onChange(getToday());
                setIsOpen(false);
              }}
              className="
                flex-1 px-3 py-1.5 rounded-lg text-sm
                text-text-secondary hover:text-text-primary
                bg-bg-secondary hover:bg-bg-tertiary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              "
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                onChange(yesterday.toISOString().split('T')[0]);
                setIsOpen(false);
              }}
              className="
                flex-1 px-3 py-1.5 rounded-lg text-sm
                text-text-secondary hover:text-text-primary
                bg-bg-secondary hover:bg-bg-tertiary
                transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              "
            >
              Yesterday
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
